<template>
    <div class="grid-wrapper margin--2xl">
        <ResourceFilters
            :topic="filters.topic"
            :species="filters.species"
            :tag="filters.tag"
            :search="filters.search"
            :topics="config.topics"
            :species-terms="config.species"
            :has-filters="hasFilters"
            :page-url="config.pageUrl"
            :clear-topic-url="buildUrl({ topic: '' })"
            :clear-species-url="buildUrl({ species: '' })"
            :icons="config.icons"
            :i18n="config.i18n"
            @update:topic="applyFilter('topic', $event)"
            @update:species="applyFilter('species', $event)"
            @update:search="applyFilter('search', $event)"
            @reset="reset"
        />
    </div>

    <ResourceGrid
        v-if="items.length"
        ref="grid"
        :items="items"
        :total="total"
        :icons="config.icons"
        :i18n="config.i18n"
    >
        <ResourceLoadMore
            v-if="hasMore || loading"
            :loading="loading"
            :next-url="nextUrl"
            :label="config.i18n.loadMore"
            @load="loadMore"
        />
    </ResourceGrid>

    <div v-else class="grid-wrapper margin--3xl">
        <div class="search-results__empty" role="status">
            <span v-html="config.icons.info"></span>
            <h5 class="body--large">
                {{ config.i18n.noResults }}
            </h5>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, reactive, ref } from 'vue'

import ResourceFilters from './ResourceFilters.vue'
import ResourceGrid from './ResourceGrid.vue'
import ResourceLoadMore from './ResourceLoadMore.vue'

const props = defineProps({
    config: { type: Object, required: true },
})

const config = props.config

const filters = reactive({
    topic: config.filters.topic || '',
    species: config.filters.species || '',
    tag: config.filters.tag || '',
    search: config.filters.search || '',
})

const items = ref(config.items || [])
const total = ref(config.total || 0)
const maxPages = ref(config.maxPages || 0)
const page = ref(config.page || 1)
const loading = ref(false)
const grid = ref(null)

let request = null

const hasFilters = computed(
    () => filters.topic !== '' || filters.species !== '' || filters.tag !== '' || filters.search !== ''
)

const hasMore = computed(() => page.value < maxPages.value)

const queryString = (overrides = {}) => {
    const state = { ...filters, ...overrides }
    const params = new URLSearchParams()

    if (state.topic) {
        params.set('resource_topic', state.topic)
    }

    if (state.species) {
        params.set('resource_species', state.species)
    }

    if (state.tag) {
        params.set('resource_tag', state.tag)
    }

    if (state.search) {
        params.set('resource_search', state.search)
    }

    const query = params.toString()

    return query ? `?${query}` : ''
}

const buildUrl = (overrides = {}) => config.pageUrl + queryString(overrides)

const nextUrl = computed(() => {
    const base = config.pageUrl.replace(/\/?$/, '/')

    return `${base}page/${page.value + 1}/${queryString()}`
})

const fetchPage = async (requested, { append }) => {
    if (request) {
        request.abort()
    }

    request = new AbortController()
    loading.value = true

    const params = new URLSearchParams({
        topic: filters.topic,
        species: filters.species,
        tag: filters.tag,
        search: filters.search,
        page: String(requested),
        per_page: String(config.perPage),
    })

    if (config.lang) {
        params.set('lang', config.lang)
    }

    if (!hasFilters.value) {
        ;(config.exclude || []).forEach((id) => params.append('exclude[]', String(id)))
    }

    try {
        const response = await fetch(`${config.restUrl}?${params.toString()}`, {
            signal: request.signal,
            headers: { Accept: 'application/json' },
        })

        if (!response.ok) {
            throw new Error(`Request failed with ${response.status}`)
        }

        const data = await response.json()
        const previous = items.value.length

        items.value = append ? items.value.concat(data.items) : data.items
        total.value = data.total
        maxPages.value = data.max_pages
        page.value = data.page

        if (append) {
            await nextTick()
            grid.value?.focusItemAt(previous)
        }

        loading.value = false
        request = null
    } catch (error) {
        if (error.name !== 'AbortError') {
            loading.value = false
            request = null
        }
    }
}

const applyFilter = (key, value) => {
    if (key === 'search') {
        value = (value || '').trim().replace(/\s+/g, ' ')
    }

    if (filters[key] === value) {
        return
    }

    filters[key] = value

    window.history.pushState({ ...filters }, '', buildUrl())

    fetchPage(1, { append: false })
}

const reset = () => {
    if (!hasFilters.value) {
        return
    }

    filters.topic = ''
    filters.species = ''
    filters.tag = ''
    filters.search = ''

    window.history.pushState({ ...filters }, '', config.pageUrl)

    fetchPage(1, { append: false })
}

const loadMore = () => {
    if (loading.value || !hasMore.value) {
        return
    }

    fetchPage(page.value + 1, { append: true })
}

const onPopState = () => {
    const params = new URLSearchParams(window.location.search)

    filters.topic = params.get('resource_topic') || ''
    filters.species = params.get('resource_species') || ''
    filters.tag = params.get('resource_tag') || ''
    filters.search = (params.get('resource_search') || '').trim()

    fetchPage(1, { append: false })
}

onMounted(() => {
    window.addEventListener('popstate', onPopState)
})

onBeforeUnmount(() => {
    window.removeEventListener('popstate', onPopState)
    request?.abort()
})
</script>
